import { priceStay } from '../../tariff/src/engine';
import type { PieceSet } from '../../tariff/src/types';

/**
 * Does the price per night ever RISE as a stay gets longer?
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS THE QUESTION A CHANNEL ASKS
 * ---------------------------------------------------------------------------
 *
 * A channel consumes totals per (date, length). It does not see, and cannot
 * express, what the park's own arithmetic did to get there — so the constraint
 * that matters to Booking.com or Airbnb is not "how is this built" but "does a
 * longer stay ever cost more per night than a shorter one". A length discount
 * that goes backwards is what a feed rejects, or silently renders as a price
 * rise nobody meant to advertise.
 *
 * The distinction is worth being exact about, because a park's own instinct is
 * usually about the MARGINAL night and a channel's rule is about the AVERAGE.
 * De Vergarde's rentals are a good example of the two disagreeing: nights four
 * to seven cost about €29 each and nights eight onward about €50, so the
 * marginal price doubles — and the average per night falls the whole way,
 * €93 at three nights to €50 at twenty-one. Nothing a channel could object to,
 * on a shape the operator reasonably described as "the price goes up".
 *
 * ---------------------------------------------------------------------------
 * WHAT IT DELIBERATELY DOES NOT JUDGE
 * ---------------------------------------------------------------------------
 *
 * The shape of the marginal curve, which is the park's business and is where
 * every arrangement lives. A weekend package dearer per night than a fortnight
 * is normal and correct.
 *
 * And lengths a card refuses. A stay it will not sell is not a stay priced
 * badly, and reporting one would make a three-night minimum look like a defect.
 */

export interface PerNightRise {
  /** The shorter stay, and the longer one that costs more per night. */
  shorter: number;
  longer: number;
  shorterPerNight: number;
  longerPerNight: number;
  arrival: number;
}

export interface MonotonicOptions {
  from: number;
  /** Exclusive. */
  until: number;
  lengths: number[];
  quantities?: Record<string, number>;
  /** Report at most this many; one example per arrival is enough to act on. */
  limit?: number;
}

/**
 * Every arrival where a longer stay costs more per night than a shorter one.
 *
 * Compared against the CHEAPEST per-night seen so far rather than against the
 * immediately shorter stay: a channel showing "from €X per night" takes the
 * best on offer, and a dip at seven nights followed by a rise at eight is a
 * rise whether or not six nights happened to be dearer still.
 */
export function findPerNightRises(
  set: PieceSet, opts: MonotonicOptions,
): PerNightRise[] {
  const lengths = [...opts.lengths].sort((a, b) => a - b);
  const quantities = opts.quantities ?? {};
  const limit = opts.limit ?? 50;
  const out: PerNightRise[] = [];

  for (let arrival = opts.from; arrival < opts.until && out.length < limit; arrival += 1) {
    let best: { nights: number; perNight: number } | null = null;

    for (const nights of lengths) {
      let total: number | null = null;
      try {
        const q = priceStay(set, { arrival, nights, quantities });
        total = q.status === 'bookable' ? q.total : null;
      } catch { total = null; }
      // A length this card refuses says nothing about the length curve.
      if (total === null || nights <= 0) continue;

      const perNight = total / nights;
      if (best !== null && perNight > best.perNight + 0.005) {
        out.push({
          arrival,
          shorter: best.nights,
          longer: nights,
          shorterPerNight: round(best.perNight),
          longerPerNight: round(perNight),
        });
        break;
      }
      if (best === null || perNight < best.perNight) best = { nights, perNight };
    }
  }
  return out;
}

const round = (n: number): number => Math.round(n * 100) / 100;
